import { useState, useEffect, useRef } from 'react';
import supabase from '../supabaseClient';
import { SHIFT_CONFIGS } from '../constants/shiftData';
import dayjs from 'dayjs';

// Shared cache for alerts across all components
let alertsCache = {
  data: [],
  loading: false,
  lastFetched: null,
  fetchPromise: null,
};

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

const useAdminAlerts = () => {
  const [alerts, setAlerts] = useState(alertsCache.data);
  const [loading, setLoading] = useState(alertsCache.loading || alertsCache.data.length === 0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    
    // Check if cache is valid
    const now = Date.now();
    const isCacheValid = alertsCache.lastFetched && 
                        (now - alertsCache.lastFetched) < CACHE_DURATION &&
                        alertsCache.data.length >= 0;

    if (isCacheValid && alertsCache.data.length >= 0) {
      // Use cached data
      setAlerts(alertsCache.data);
      setLoading(false);
    } else if (!alertsCache.fetchPromise) {
      // Start fetching if not already fetching
      fetchAlerts();
    } else {
      // Wait for existing fetch to complete
      alertsCache.fetchPromise.then(() => {
        if (isMountedRef.current) {
          setAlerts(alertsCache.data);
          setLoading(false);
        }
      });
    }

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchAlerts = async () => {
    // If already fetching, return the existing promise
    if (alertsCache.fetchPromise) {
      return alertsCache.fetchPromise;
    }

    // Create new fetch promise
    alertsCache.fetchPromise = (async () => {
      try {
        alertsCache.loading = true;
        if (isMountedRef.current) {
          setLoading(true);
        }
        
        const today = dayjs().format('YYYY-MM-DD');
      const todayDate = new Date(today);
      const dayOfWeek = todayDate.getDay();

      const alertResults = [];

      // 1. Students absent today
      const { count: absentCount } = await supabase
        .from('attendance')
        .select('id', { count: 'exact', head: true })
        .eq('date', today)
        .eq('status', 'Absent');

      if (absentCount > 0) {
        alertResults.push({
          id: 'students-absent-today',
          type: 'attendance',
          severity: 'warning',
          count: absentCount,
          message: `${absentCount} student${absentCount !== 1 ? 's' : ''} absent today`,
          actionUrl: '/attendance',
        });
      }

      // 2. Teachers haven't submitted attendance today
      // Get all classes that should have class today
      const { data: allClasses } = await supabase
        .from('classes')
        .select('id, name, shift, teacher_id, teachers:teacher_id(id, first_name, last_name)')
        .eq('is_active', true);

      // Filter classes that should have class today based on shift
      const classesToday = allClasses?.filter((cls) => {
        const shiftConfig = SHIFT_CONFIGS[cls.shift];
        return shiftConfig && shiftConfig.workingDays.includes(dayOfWeek);
      }) || [];

      if (allClasses && classesToday.length > 0) {

        // Check for substitute assignments that might affect today
        const { data: substituteAssignments } = await supabase
          .from('substitute_assignments')
          .select('class_id, substitute_teacher_id, classes:class_id(shift)')
          .eq('status', 'pending')
          .lte('start_date', today)
          .gte('end_date', today);

        const substituteTeacherIds = new Map(
          substituteAssignments?.map((sub) => [sub.class_id, sub.substitute_teacher_id]) || []
        );

        // Get all attendance records for today
        const { data: attendanceToday } = await supabase
          .from('attendance')
          .select('class_id, created_by')
          .eq('date', today);

        const classesWithAttendance = new Set(
          attendanceToday?.map((a) => `${a.class_id}-${a.created_by}`) || []
        );

        // Find teachers who should have submitted but haven't
        const teachersExpected = new Set();
        classesToday.forEach((cls) => {
          const effectiveTeacherId = substituteTeacherIds.get(cls.id) || cls.teacher_id;
          if (effectiveTeacherId) {
            teachersExpected.add(effectiveTeacherId);
          }
        });

        const teachersWithoutAttendance = [];
        teachersExpected.forEach((teacherId) => {
          const hasAttendance = Array.from(classesWithAttendance).some((key) => {
            const [createdBy] = key.split('-');
            return createdBy === teacherId;
          });
          if (!hasAttendance) {
            const teacher = allClasses
              .flatMap((cls) => {
                if (cls.teacher_id === teacherId) return [cls.teachers];
                if (substituteTeacherIds.get(cls.id) === teacherId) {
                  const sub = substituteAssignments?.find((s) => s.substitute_teacher_id === teacherId);
                  return sub ? [{ id: teacherId }] : [];
                }
                return [];
              })
              .find((t) => t?.id === teacherId);
            if (teacher) {
              teachersWithoutAttendance.push(teacherId);
            }
          }
        });

        if (teachersWithoutAttendance.length > 0) {
          alertResults.push({
            id: 'teachers-no-attendance-today',
            type: 'teacher-activity',
            severity: 'warning',
            count: teachersWithoutAttendance.length,
            message: `${teachersWithoutAttendance.length} teacher${teachersWithoutAttendance.length !== 1 ? 's' : ''} haven't submitted attendance`,
            actionUrl: '/admin/teacher-activity',
          });
        }
      }

      // 3. Quran students missing progress today
      if (classesToday.length > 0) {
        const classIdsToday = classesToday.map((c) => c.id);
        
        // Get all Quran students who should have had class today
        const { data: quranStudents } = await supabase
          .from('students')
          .select('id, class_ids, is_quran_student')
          .eq('is_active', true)
          .eq('is_quran_student', true);

        if (quranStudents && quranStudents.length > 0) {
          // Filter students who have class today
          const studentsWithClassToday = quranStudents.filter((student) => {
            const studentClassIds = student.class_ids || [];
            return studentClassIds.some((cid) => classIdsToday.includes(cid));
          });

          if (studentsWithClassToday.length > 0) {
            const studentIds = studentsWithClassToday.map((s) => s.id);
            
            // Get all Quran progress entries for today for these students
            const { data: progressToday } = await supabase
              .from('student_quran_progress')
              .select('student_id')
              .eq('date', today)
              .in('student_id', studentIds);

            const studentsWithProgress = new Set(
              progressToday?.map((p) => p.student_id) || []
            );

            const missingProgressCount = studentsWithClassToday.filter(
              (student) => !studentsWithProgress.has(student.id)
            ).length;

            if (missingProgressCount > 0) {
              alertResults.push({
                id: 'quran-progress-missing-today',
                type: 'progress',
                severity: 'info',
                count: missingProgressCount,
                message: `Quran progress missing for ${missingProgressCount} student${missingProgressCount !== 1 ? 's' : ''}`,
                actionUrl: '/attendance',
              });
            }
          }
        }
      }

      // 4. Pending coverage requests
      const { count: coverageCount } = await supabase
        .from('coverage_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (coverageCount > 0) {
        alertResults.push({
          id: 'pending-coverage-requests',
          type: 'administrative',
          severity: 'info',
          count: coverageCount,
          message: `${coverageCount} pending coverage request${coverageCount !== 1 ? 's' : ''}`,
          actionUrl: '/admin/substitute-management',
        });
      }

      // 5. Pending user registrations
      const { count: pendingUsersCount } = await supabase
        .from('pending_users')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (pendingUsersCount > 0) {
        alertResults.push({
          id: 'pending-user-registrations',
          type: 'administrative',
          severity: 'info',
          count: pendingUsersCount,
          message: `${pendingUsersCount} pending user registration${pendingUsersCount !== 1 ? 's' : ''}`,
          actionUrl: '/admin/pending',
        });
      }

        // Update cache
        alertsCache.data = alertResults;
        alertsCache.lastFetched = Date.now();
        alertsCache.loading = false;
        
        // Update state if component is still mounted
        if (isMountedRef.current) {
          setAlerts(alertResults);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching alerts:', error);
        alertsCache.data = [];
        alertsCache.loading = false;
        
        // Update state if component is still mounted
        if (isMountedRef.current) {
          setAlerts([]);
          setLoading(false);
        }
      } finally {
        // Clear fetch promise so next fetch can start
        alertsCache.fetchPromise = null;
      }
    })();

    return alertsCache.fetchPromise;
  };

  const refetch = () => {
    // Invalidate cache and refetch
    alertsCache.lastFetched = null;
    alertsCache.data = [];
    return fetchAlerts();
  };

  return { alerts, loading, refetch };
};

export default useAdminAlerts;

